
import { namespace } from "vuex-class";
import { Component, Provide, Vue } from "vue-property-decorator";
import { vuex } from "@/store";
import router from "@/router";
import rest from "@/rest";
import _ from "lodash";
import { signalrEventBus } from "@/main";
// import wretch, { WretcherError } from "wretch";

// Components
import CourseCreator from "@/components/ContentCreator/CourseCreator.vue";
import EditBundleDlg from "@/components/ContentCreator/EditBundleDlg.vue";
import AiCourseWizardDlg from "@/components/ContentCreator/AiCourseWizardDlg.vue";
import AiChatModule from "@/components/ContentCreator/AiChatModule.vue";

// Interfaces
import { ExBundleAssignmentVM } from "@/interfaces/ExBundleAssignmentGroupVM";
import { ExerciseVM } from "@/interfaces/ExerciseVM";
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import { ChatMessageVm } from "@/interfaces/AiCourseWizardVm";

const auth = namespace("auth");
const ux = namespace("ux");
const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {
    CourseCreator,
    EditBundleDlg,
    AiCourseWizardDlg,
    AiChatModule
  }
})
export default class ContentCreatorBundleView extends Vue {
  @customization.Getter customizationSettings!: CustomizationVm;
  @auth.Getter isAdmin: any;
  @globals.Getter selectedBundle: any;
  @globals.Getter selectedLanguageCode!: string | null;
  @globals.Getter selectedExercise!: ExerciseVM;
  @globals.Action loadModulePages!: () => Promise<void>;
  @globals.Action saveModulePages!: (boolean) => Promise<void>;
  @globals.Action setTranslationRequiredOnBundle!: (boolean) => Promise<void>;
  @globals.Action refreshSelectedBundle!: () => Promise<void>;

  showEditBundleDlg: boolean = false;
  showCourseWizardDlg: boolean = false;
  showChatDialog: boolean = false;
  showChatMenu: boolean = false;
  bundleZipCreationBusy = false;
  isTranslationBusy = false;
  translationProgress = 0;
  chatMessages: ChatMessageVm[] = [];
  isChatBusy = false;
  guid: string = "";

  $refs!: {
    courseCreator: CourseCreator
  };

  async mounted() {
    // Initialize selected language to a valid bundle language (master or translation)
    if (this.selectedBundle?.masterLanguageCode) {
      const validCodes = new Set<string>();
      if (this.selectedBundle.masterLanguageCode) validCodes.add(this.selectedBundle.masterLanguageCode);
      if (this.selectedBundle.translationLanguageCodes && Array.isArray(this.selectedBundle.translationLanguageCodes)) {
        this.selectedBundle.translationLanguageCodes.forEach((c: string) => validCodes.add(c));
      }

      if (!this.selectedLanguageCode || !validCodes.has(this.selectedLanguageCode)) {
        await vuex.globals.setSelectedLanguageCode(this.selectedBundle.masterLanguageCode);
      }
    }

    this.initChat();

    // Ensure overlays don't conflict when breakpoint changes
    this.$watch(() => this.$vuetify.breakpoint.xsOnly, (isXs: boolean) => {
      if (isXs) {
        // entering XS: close menu if open
        this.showChatMenu = false;
      } else {
        // leaving XS: close dialog if open
        this.showChatDialog = false;
      }
    });
  }

  created() {
    // SignalR event listener
    signalrEventBus.$on('zipArchiveCreationFinished', this.zipArchiveCreationFinished);
    signalrEventBus.$on('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  beforeDestroy() {
    // clearAllBodyScrollLocks();
    // Make sure to cleanup SignalR event handlers when removing the component
    signalrEventBus.$off('zipArchiveCreationFinished', this.zipArchiveCreationFinished);
    signalrEventBus.$off('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  updateView() {
  }

  // SignalR events
  zipArchiveCreationFinished(response: any) {
    console.log("ContentCreatorBundleView > Zip archive creation finished:", response.zipFilePath + " " + response.message);
    if (response.status == "SUC") {
      this.$globalHelper.downloadFile(response.zipFilePath);
    } else {
    }

    // this.$emit('content:ZipArchiveCreationFinished');
    this.bundleZipCreationBusy = false;
  }

  async backgroundTaskEvent(response: { id: string, progress: number, message: string, status: string }) {
    if (response.id == "Translation") {
      if (response.status == "INF") {
        this.translationProgress = response.progress;
        // vuex.ux.SB_SUCCESS({
        //   message: response.message,
        //   timeout: 4000
        // });
        return;
      }

      if (response.status == "SUC") {
        this.isTranslationBusy = false;
        this.translationProgress = 0;
        // await this.setTranslationRequiredOnBundle(false);
        // await this.loadModulePages();
        await this.refreshSelectedBundle();
        this.$root.$emit('updateModules:CourseModules');
        this.onBundleChanged()
        vuex.ux.SB_SUCCESS({
          message: response.message,
          timeout: 3000
        });
        return;
      }

      if (response.status == "ERR") {
        this.isTranslationBusy = false;
        this.translationProgress = 0;
        vuex.ux.SB_FAILURE({
          message: response.message,
          timeout: 0
        });
        return;
      }
    }

    if (this.guid !== response.id) return;
    console.log("ContentCreatorBundleView > Background task event:", response);
    if (response.status == "INF") {
      vuex.ux.SB_SUCCESS({
        message: response.message,
        timeout: 1500
      });
      return;
    }
  }

  initChat() {
    const welcomeMessage: ChatMessageVm = {
      id: 1,
      message: "Hallo, ich bin der L3RN online Support Assistent! Wie kann ich Ihnen bei der Erstellung Ihrer Schulung helfen?",
      fromAi: true
    };
    this.chatMessages = [welcomeMessage];
  }

  openChat() {
    if (this.$vuetify.breakpoint.xsOnly) {
      this.showChatDialog = true;
    } else {
      this.showChatMenu = true;
    }
  }

  async onOpenCourseWizardDlg() {
    await this.saveModulePages(false);
    this.showCourseWizardDlg = true
  }

  async onNewChatMessage(message: ChatMessageVm) {
    this.isChatBusy = true;
    await rest.url("contentManager/aiSupportChat")
      .post(this.chatMessages)
      .then((response) => {
        console.log("ContentCreatorBundleView > AI Support Chat response:", response);

        // add new chat messages from response to chatMessages
        let countOfNewMessages = response.length - this.chatMessages.length;
        let newMessages = response.slice(-countOfNewMessages);
        let i = this.chatMessages.length + 1;
        newMessages.forEach(m => {
          this.chatMessages.push({
            id: i++,
            message: m.message,
            fromAi: m.fromAi
          });
        });
      })
      .finally(() => {
        this.isChatBusy = false;
      });
  }

  get languageOptions() {
    return this.$globalHelper.euLanguageOptions;
  }

  getLanguageText(code: string): string {
    const lang = this.languageOptions.find(l => l.value === code);
    return lang ? lang.text : code;
  }

  getFlagUrlForLanguage(code: string): string {
    const lang = this.languageOptions.find(l => l.value === code);
    if (lang?.countryCode) {
      return this.$globalHelper.getFlagUrl(lang.countryCode);
    }
    return '';
  }

  get languageChipColor(): string {
    const bundle = this.selectedBundle;
    const code = this.selectedLanguageCode;
    if (bundle && code && bundle.masterLanguageCode && code !== bundle.masterLanguageCode) {
      return this.$store.state.ux.colorError;
    }
    return this.$store.state.ux.colorAccent;
  }

  // Return translated bundle name based on selected language (fallback to bundle.name)
  get translatedBundleName(): string {
    const bundle = this.selectedBundle;
    if (!bundle) return '';
    const names = bundle.nameTranslations as Record<string,string> | undefined;
    const code = this.selectedLanguageCode || '';
    if (names && code && names[code] && names[code].length > 0) {
      return names[code];
    }
    return bundle.name || '';
  }

  // Return translated selected exercise name (fallback to selectedExercise.name)
  get translatedSelectedExerciseName(): string {
    const ex = this.selectedExercise;
    if (!ex) return '';
    const names = (ex as any).nameTranslations as Record<string,string> | undefined;
    const code = this.selectedLanguageCode || '';
    if (names && code && names[code] && names[code].length > 0) {
      return names[code];
    }
    return ex.name || '';
  }

  async setLanguageCode(code: string) {
    console.log("ContentCreatorBundleView > Setting language code to", code);
    await this.saveModulePages(false);
    await vuex.globals.setSelectedLanguageCode(code);
    await this.loadModulePages();
  }

  async onEditBundle() {
    await this.saveModulePages(false);
    this.showEditBundleDlg = true;
  }

  async onTranslate() {
    if (this.isTranslationBusy) return;

    // First save module pages to ensure we have the latest content before starting the translation
    await this.saveModulePages(false);

    this.isTranslationBusy = true;
    await this.$l3rnOnlineHub.ensureConnection();

    let params = <ParamDictionary>{dictionary: {}};
    params.dictionary!["BundleId"] = this.selectedBundle.id.toString();
    params.dictionary!["SignalRId"] = this.$l3rnOnlineHub.ConnectionId!;

    await rest.url("contentManager/translateCourse")
      .post(params)
      .then(async (response) => {
        if (!response || response.length === 0) {
          // vuex.ux.SB_FAILURE({
          //   message: "Fehler beim Starten der Übersetzung.",
          //   timeout: 0
          // });
          return;
        }
      });
  }

  async createBundleZipFile() {
    this.bundleZipCreationBusy = true;

    await this.$l3rnOnlineHub.ensureConnection();

    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["SignalRId"] = this.$l3rnOnlineHub.ConnectionId!;
    params.dictionary!["BundleId"] =  this.selectedBundle!.id.toString();

    await rest.url("contentManager/createBundleZipFile")
      .post(params)
      .then((guid) => {
        this.guid = guid;
      });
  }

  onStepChange(index, step) {
    console.log(`Step to ${index}: ${step.title}`);
  }

  onCourseWizardComplete() {
    console.log('ContentCreatorBundleView > AI course wizard finished');
    this.$root.$emit('updateModules:CourseModules');
  }

  async onBundleChanged() {
    // console.log("ContentBundle: onBundleChanged");
    // await this.loadModulePages();
    this.$root.$emit('updateModules:CourseModules');
  }

  async showExerciseBundleForTest() {
    // Save unsaved questions
    // await this.$refs.courseCreator.saveQuestions();
    // this.selectedApproval = approval;
    let assignmentVm = <ExBundleAssignmentVM>{
      id: 0, // not needed
      exBundleId: this.selectedBundle.id,
      exBundle: this.selectedBundle,
      userId: null,
      enabled: true,
      writeResults: false,
    }
    this.$store.commit('globals/setExBundleAssignment', assignmentVm);
    router.push("/exerciseBundle");
  }

  // async generateGuid() {
  //   this.newGuid = await rest.url("contentManager/getNewGuid").get();
  //   // this.showGuidDialog = true;
  // }

  goBack() {
    router.go(-1);
  }

  // replace with vuex variant
  get backgroundColor() {
    let currentThemeName = this.$vuetify.theme.dark ? 'dark' : 'light';
    return this.$vuetify.theme.themes[currentThemeName].background;
  }
}
